import { Component, ElementRef, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatFormField, MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

import { TranslateModule } from '@ngx-translate/core';
import { Logger, LoggerFactory } from 'app/core/logger.service';
import { AccountAdminService } from 'app/back-office/account-admin.service';
import { CustomerItem } from './customer-item';
import { CustomerSelectorService } from './customer-selector.service';
import { IdentityService } from 'app/core/identity/identity.service';

@Component({
  selector: 'app-customer-selector',
  imports: [
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatFormField,
    MatIcon,
    MatInput,
    MatOption,
    MatProgressSpinner,
    TranslateModule,
  ],
  templateUrl: './customer-selector.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './customer-selector.component.scss',
})
export class CustomerSelectorComponent implements OnInit {
  private readonly logger: Logger;
  private shouldQueryForMoreCustomers: boolean = false;

  public loading: boolean = false;
  public selectedCustomer: CustomerItem | null = null;
  public selectableCustomers: CustomerItem[] = [];
  public filteredCustomers: CustomerItem[] = [];
  public onFocusName = false;

  @ViewChild('customerInput') public customerInputRef: ElementRef;
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;

  constructor(
    loggerFactory: LoggerFactory,
    private readonly accountAdminService: AccountAdminService,
    private readonly customerSelectorService: CustomerSelectorService,
    private readonly identityService: IdentityService,
  ) {
    this.logger = loggerFactory.get('CustomerSelectorComponent');
    this.selectedCustomer = new CustomerItem('', 'All Customers', []);
    this.shouldQueryForMoreCustomers = true;
  }

  public ngOnInit(): void {
    const storedCustomerId = this.customerSelectorService.getStoredCustomerId();
    if (!storedCustomerId) {
      return;
    }

    this.shouldQueryForMoreCustomers = false;
    this.loading = true;
    const siteIds = this.identityService.currentIdentity.getAllSiteIds();

    this.accountAdminService.getCustomersForDropdown(siteIds).subscribe({
      next: (allCustomers: CustomerItem[]) => {
        this.loading = false;

        if (!allCustomers || allCustomers.length === 0) {
          this.logger.info('No customers found', 'all customers:', allCustomers);
          this.selectableCustomers = [];
          return;
        }

        this.selectableCustomers = [new CustomerItem('', 'All Customers', []), ...allCustomers];
        const customer = this.selectableCustomers.find((c) => c.id === storedCustomerId);
        if (customer) {
          this.selectedCustomer = customer;
          this.customerSelectorService.setSelectedCustomer(customer);
        }
      },
      error: () => {
        this.logger.error('Failed to load customers for dropdown');
        this.loading = false;
      },
    });
  }

  public onFocus(event: any): void {
    this.onFocusName = true;
    this.queryForMoreCustomers(event);
  }

  public onKeyUp(event: any): void {
    this.setFilteredCustomers(event.target.value);
  }

  public onBlur(): void {
    this.onFocusName = false;
  }

  public onOptionClick(customerId: string): void {
    this.logger.debug(`Customer ${customerId} is clicked`);
    this.setSelectedCustomer(customerId);
    this.onFocusName = false;
  }

  private queryForMoreCustomers(event: any): void {
    if (this.shouldQueryForMoreCustomers) {
      this.shouldQueryForMoreCustomers = false;
      this.loading = true;

      const siteIds = this.identityService.currentIdentity.getAllSiteIds();

      this.accountAdminService.getCustomersForDropdown(siteIds).subscribe({
        next: (allCustomers: CustomerItem[]) => {
          this.loading = false;

          if (!allCustomers || allCustomers.length === 0) {
            this.logger.info('No customers found', 'all customers:', allCustomers);
            this.selectableCustomers = [];
            return;
          }
          this.selectableCustomers = [new CustomerItem('', 'All Customers', []), ...allCustomers];
          this.initTextBox(event);
        },
        error: () => {
          this.logger.error('Failed to load customers for dropdown');
          this.loading = false;
        },
      });
    } else {
      this.initTextBox(event);
    }
  }

  private initTextBox(event: any): void {
    event.target.value = '';
    this.setFilteredCustomers(event.target.value);
  }

  private setFilteredCustomers(currentInput: string): void {
    if (this.selectableCustomers != null) {
      this.filteredCustomers = this.selectableCustomers
        .filter((customer) => new RegExp(currentInput, 'i').test(customer.name))
        .sort((a, b) => {
          if (a.id === '') return -1;
          if (b.id === '') return 1;
          return a.name.localeCompare(b.name);
        });
    }
  }

  private setSelectedCustomer(customerId: string): void {
    if (!this.selectableCustomers) {
      return;
    }
    const customer = this.selectableCustomers.find((c) => c.id === customerId);
    if (!customer) {
      return;
    }
    this.selectedCustomer = customer;

    if (customer.id === '') {
      this.customerSelectorService.setSelectedCustomer(null);
    } else {
      const customerItem: CustomerItem = {
        id: customer.id,
        name: customer.name,
        siteIds: customer.siteIds,
      };
      this.customerSelectorService.setSelectedCustomer(customerItem);
    }
  }
}
